import { useEffect, useState } from "react";
import { Loader2, Sparkles, X, Bug, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { apiVisionAnalyze, formatApiError } from "@/lib/api";
import { toast } from "sonner";

async function dataUriToFile(dataUri, filename = "image.jpg") {
    const res = await fetch(dataUri);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

/**
 * Lightweight modal that runs vision/analyze against a single image
 * (provided as either a File, a data URI, or an existing image id +
 * data_uri pair) and renders the mentor's reply inline. Used by both
 * the Photos page and the Pests page.
 */
export default function DiagnoseDialog({
    open, onClose, source, mode = "diagnose", initialPrompt = "",
}) {
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState("");
    const [matches, setMatches] = useState([]);
    const [prompt, setPrompt] = useState(initialPrompt);

    useEffect(() => {
        if (!open) {
            setReply("");
            setMatches([]);
            setPrompt(initialPrompt);
        }
    }, [open, initialPrompt]);

    const run = async () => {
        setBusy(true);
        setReply("");
        setMatches([]);
        try {
            let file = source.file;
            if (!file && source.dataUri) {
                file = await dataUriToFile(source.dataUri, source.filename || "image.jpg");
            }
            if (!file) throw new Error("No image to analyse.");
            const result = await apiVisionAnalyze(file, { mode, prompt });
            setReply(result.reply || "");
            setMatches(result.matches || []);
            if (result.quota && !result.quota.unlimited && result.quota.remaining === 0) {
                toast("You've used your free photo diagnoses for the month.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="diagnose-dialog"
        >
            <div
                className="journal-card max-w-xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-[var(--vm-border)]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[var(--vm-accent)]/15 flex items-center justify-center">
                            {mode === "pest_match"
                                ? <Bug className="w-5 h-5 text-[var(--vm-accent)]" strokeWidth={1.6} />
                                : <Sparkles className="w-5 h-5 text-[var(--vm-accent)]" strokeWidth={1.6} />}
                        </div>
                        <div>
                            <div className="font-serif-display text-xl text-[var(--vm-text)]">
                                {mode === "pest_match" ? "Identify this critter" : "Diagnose with rooteed"}
                            </div>
                            <div className="text-xs text-[var(--vm-text-muted)]">
                                Powered by Gemini 2.5 Pro · Tailored to your garden
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {source.dataUri && (
                        <img
                            src={source.dataUri}
                            alt="Subject"
                            className="rounded-lg max-h-60 mx-auto border border-[var(--vm-border)]"
                        />
                    )}
                    {!reply && !busy && (
                        <>
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">
                                    Any question to start (optional)
                                </label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows={2}
                                    data-testid="diagnose-prompt-input"
                                    placeholder={mode === "pest_match"
                                        ? "Is this helpful or a problem?"
                                        : "What's wrong with this plant?"}
                                    className="w-full text-sm bg-[var(--vm-input)] border border-[var(--vm-border)] rounded-md p-2 resize-none focus:outline-none focus:border-[var(--vm-accent)]"
                                />
                            </div>
                            <Button
                                onClick={run}
                                data-testid="diagnose-run-btn"
                                className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                            >
                                <Sparkles className="w-4 h-4 mr-2" />
                                Ask rooteed
                            </Button>
                        </>
                    )}

                    {busy && (
                        <div className="text-sm text-[var(--vm-text-muted)] flex items-center gap-2 py-6 justify-center" data-testid="diagnose-loading">
                            <Loader2 className="w-4 h-4 animate-spin" /> Looking at your photo…
                        </div>
                    )}

                    {reply && (
                        <div className="space-y-3" data-testid="diagnose-reply">
                            <div className="bubble-ai text-sm leading-relaxed whitespace-pre-wrap">
                                {reply}
                            </div>
                            {matches.length > 0 && (
                                <div className="space-y-2 pt-2 border-t border-[var(--vm-border)]">
                                    <div className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Matches in your catalog</div>
                                    {matches.map((m) => (
                                        <Link
                                            key={m.slug}
                                            to={`/pests/${m.slug}`}
                                            onClick={onClose}
                                            data-testid={`diagnose-match-${m.slug}`}
                                            className="flex items-center justify-between p-2 rounded-md hover:bg-[var(--vm-input)] text-sm text-[var(--vm-text)]"
                                        >
                                            <span>
                                                <span className="font-medium">{m.common_name}</span>
                                                <span className="text-xs text-[var(--vm-text-muted)] ml-2">
                                                    {m.role === "beneficial" ? "beneficial" : "pest"}
                                                </span>
                                            </span>
                                            <ArrowRight className="w-4 h-4 text-[var(--vm-text-muted)]" />
                                        </Link>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2 pt-1">
                                <Button
                                    onClick={() => { setReply(""); setMatches([]); }}
                                    variant="outline"
                                    className="flex-1 rounded-full border-[var(--vm-border)]"
                                    data-testid="diagnose-ask-again-btn"
                                >
                                    Ask again
                                </Button>
                                <Button
                                    onClick={onClose}
                                    className="flex-1 rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                                    data-testid="diagnose-close-btn"
                                >
                                    Done
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
